import { sqItemsApi } from '@/sdk/api/ItemsApi';
import { isVantageNameBasedAssetContextEnabled } from '@/services/systemConfiguration.utilities';
import { safeGetJsonProperty } from '@/utilities/httpHelpers.utilities';
import { VantageNames } from '@/vantage/constants';
import editManualEvent from '@/vantage/extensions/editManualEvent.json';
import flag from '@/vantage/extensions/flag.json';
import labels from '@/vantage/extensions/labels.json';
import notes from '@/vantage/extensions/notes.json';
import reviewed from '@/vantage/extensions/reviewed.json';
import suppression from '@/vantage/extensions/suppression.json';
import { upgradeExtensions } from '@/vantage/extensionsUpgrader';
import type { ExtensionSchema } from '@/vantage/types/Extension.types';

export const bundledExtensions = [flag, labels, notes, reviewed, suppression, editManualEvent] as ExtensionSchema[];

export const ALWAYS_INCLUDED_EXTENSIONS = [
  VantageNames.Extensions.Flag,
  VantageNames.Extensions.Reviewed,
  VantageNames.Extensions.Labels,
  VantageNames.Extensions.Suppression,
  VantageNames.Extensions.EditManualEvent,
];

/** Daily */
export const DEFAULT_CRON_SCHEDULE = ['0 0 0 ? * 1,2,3,4,5,6,7'];

export function getExtensionConfigs(extensions: ExtensionSchema[]) {
  let install: ExtensionSchema[] = [];
  let enabled = extensions.filter(({ enabled }) => enabled);

  if (!extensions.length) {
    install = [...bundledExtensions];
    enabled = [...bundledExtensions].map((ext) => ({ ...ext, enabled: true }) as ExtensionSchema);
    return { install, enabled };
  }

  if (!enabled.some((ext) => ext.name === VantageNames.Extensions.Notes)) {
    install.push(notes as ExtensionSchema);
    enabled.push(notes as ExtensionSchema);
  }

  for (const extensionName of ALWAYS_INCLUDED_EXTENSIONS) {
    if (!extensions.some((ext) => ext.name === extensionName)) {
      const extension = bundledExtensions.find((ext) => ext.name === extensionName);
      install.push({ ...extension, enabled: false } as ExtensionSchema);
    }
  }

  return { install, enabled: enabled.map(upgradeExtensions) };
}

export async function installExtensions(workbookId: string, extensions: ExtensionSchema[]) {
  const installedExtensions: ExtensionSchema[] = await safeGetJsonProperty(workbookId, 'extensions', []);
  await sqItemsApi.setProperties(
    [{ name: 'extensions', value: JSON.stringify(installedExtensions.concat(extensions)) }],
    {
      id: workbookId,
    },
  );
}

export const tableConfig = () => ({
  purviewFields: [VantageNames.Columns.Condition, VantageNames.Columns.Asset],
  uniquenessMetaContextMappings: {
    Condition: VantageNames.Columns.ConditionID,
    Asset: VantageNames.Columns.AssetUUID,
  },
  defaultDateRange: {
    lookback: '2 days',
    lookahead: '14 days',
    clockOverride: '3:00:00',
  },
  contextItemIdMapping: {
    Asset: VantageNames.Columns.AssetUUID,
  },
});

export async function initializeVantage(workbookId: string) {
  const isManualEventsEnabled = await safeGetJsonProperty(workbookId, 'isManualEventsEnabled', false);
  const properties = {
    extensions: bundledExtensions,
    tableConfig: tableConfig(),
    includeExtinct: false,
  };
  await sqItemsApi.setProperties(
    [
      { name: 'extensions', value: JSON.stringify(properties.extensions) },
      { name: 'includeExtinct', value: JSON.stringify(properties.includeExtinct) },
      { name: 'tableConfig', value: JSON.stringify(properties.tableConfig) },
    ],
    {
      id: workbookId,
    },
  );
  return {
    workbookId,
    ...properties,
    nameBasedAssetContextOverride: isVantageNameBasedAssetContextEnabled() as unknown as boolean,
    isManualEventsEnabled,
  };
}
